import { Component, computed, ElementRef, inject, input, output, signal, viewChild } from '@angular/core';
import { Option } from '@cumlaude/shared-utils';
import { Dialog, DialogRef } from '@angular/cdk/dialog';
import { Overlay } from '@angular/cdk/overlay';
import { DuoSelectDropdownDialogComponent, DuoSelectDropdownResponse } from '@cumlaude/shared-components-dialogs';
import { firstValueFrom } from 'rxjs';
import { IconComponent } from '@cumlaude/shared-components-icons';

@Component({
	selector: 'app-duo-select-dropdown',
	templateUrl: './duo-select-dropdown.component.html',
	styleUrls: ['./duo-select-dropdown.component.scss'],
	imports: [IconComponent],
})
export class DuoSelectDropdownComponent<A, B> {
	private readonly dialog = inject(Dialog);
	private readonly overlay = inject(Overlay);

	private dialogRef: DialogRef<DuoSelectDropdownResponse<A, B>, DuoSelectDropdownDialogComponent<A, B>> | undefined;

	readonly showDropdown = signal(false);
	readonly isHovered = signal(false);

	protected readonly iconName = computed(() => (this.showDropdown() ? 'chevron-up' : 'chevron-down'));

	protected readonly iconColor = computed(() => (this.showDropdown() || this.isHovered() ? 'primary-1' : 'typography-5'));

	readonly optiesA = input.required<Option<A>[]>();

	readonly optiesB = input.required<Option<B>[]>();

	readonly selectedOptieA = input.required<Option<A>>();

	readonly selectedOptieB = input.required<Option<B>>();

	readonly optieAHeader = input.required<string>();

	readonly optieBHeader = input.required<string>();

	readonly label = input<string>();

	optieAClicked = output<A>();

	optieBClicked = output<B>();

	readonly dropdownHostElement = viewChild.required<ElementRef>('dropdownHost');

	readonly huidigeWaarde = computed(() => {
		const waardeA = this.selectedOptieA();
		const waardeB = this.selectedOptieB();
		return `${waardeA.text}, ${waardeB.text}`;
	});

	protected async openDialog() {
		this.showDropdown.set(true);

		const dropdownElement = this.dropdownHostElement();
		const boundingClientRect = dropdownElement.nativeElement.getBoundingClientRect();

		this.dialogRef = this.dialog.open(DuoSelectDropdownDialogComponent<A, B>, {
			data: {
				optiesA: this.optiesA(),
				optiesB: this.optiesB(),
				selectedOptieA: this.selectedOptieA(),
				selectedOptieB: this.selectedOptieB(),
				optieAHeader: this.optieAHeader(),
				optieBHeader: this.optieBHeader(),
				label: this.label(),
			},
			backdropClass: 'cdk-overlay-transparent-backdrop',
			positionStrategy: this.overlay
				.position()
				.flexibleConnectedTo(dropdownElement)
				.withFlexibleDimensions(false)
				.withPositions([
					{
						originX: 'center',
						originY: 'bottom',
						overlayX: 'center',
						overlayY: 'top',
						offsetY: 1,
					},
					{
						originX: 'center',
						originY: 'top',
						overlayX: 'center',
						overlayY: 'bottom',
						offsetY: -1,
					},
				]),
			width: boundingClientRect.width,
		});

		const result = await firstValueFrom(this.dialogRef.closed);
		if (result) {
			const { optieA, optieB } = result;
			if (optieA) this.optieAClicked.emit(optieA.value);
			if (optieB) this.optieBClicked.emit(optieB.value);
		}

		this.showDropdown.set(false);
	}
}
